<script>
  import { tick } from 'svelte';

  import { InlineNotification, Tile } from 'carbon-components-svelte';
  import { Router, Route } from 'svelte-navigator';

  import Layout from './Layout.svelte';
  import BasicGrid from './components/BasicGrid.svelte';
  import RouteHeader from './components/RouteHeader.svelte';
  import ActivityMonitor from './components/ActivityMonitor.svelte';

  import Home from './pages/Home.svelte';
  import AdminDashboardModule from './pages/admin/AdminDashboardModule.svelte';
  import CustomerBuilds from './pages/customer-builds/CustomerBuilds.svelte';
  import CreateOrganization from './pages/organizations/CreateOrganization.svelte';
  import Emails from './pages/emails/Emails.svelte';
  import ObjectRestore from './pages/data-feeds/restore-service/ObjectRestore.svelte';
  import ObjectRestoreApproval from './pages/data-feeds/restore-service/ObjectRestoreApproval.svelte';
  import OrganizationDetails from './pages/organizations/OrganizationDetails.svelte';
  import OrganizationList from './pages/organizations/OrganizationList.svelte';
  import CreateGroup from './pages/groups/CreateGroup.svelte';
  import CreateUser from './pages/users/CreateUser.svelte';
  import DataFeedModule from './pages/data-feeds/DataFeedModule.svelte';
  import DataFeedDetails from './pages/data-feeds/details/DataFeedDetails.svelte';
  import GroupDetails from './pages/groups/GroupDetails.svelte';
  import GroupList from './pages/groups/GroupList.svelte';
  import Licenses from './pages/licenses/Licenses.svelte';
  import TaskModule from './pages/tasks/TaskModule.svelte';
  import SlaTaskDetails from './pages/data-feeds/sla/SlaTaskDetails.svelte';
  import UserDetails from './pages/users/UserDetails.svelte';
  import UserList from './pages/users/UserList.svelte';
  import WorkbenchModule from './pages/workbench/WorkbenchModule.svelte';
  import ReportsModule from './pages/reports/ReportsModule.svelte';

  import routes from '../shared/routes';
  import { appConfig, userSession } from './stores';
  import { ROOT_SERVER_URL } from './config';

  $: tick().then(async () => {
    if ($appConfig.loading || $appConfig.error || $userSession.loading || $userSession.error) {
      return;
    }

    if (!$userSession.session) {
      console.warn('[App] User does not have a valid session! Redirecting to Hosted UI...');
      const callbackUrl = encodeURIComponent(window.location.href);
      window.location.href = `${ROOT_SERVER_URL}/api/authorization/login?callback=${callbackUrl}`;
      return;
    }

    window.mst.closeLoader();
  });
</script>

<Router primary={false}>
  <ActivityMonitor />
  <Layout>
    {#if $appConfig.error || $userSession.error}
      <Tile>
        <InlineNotification kind="error" title="Error:" subtitle="The service is unavailable." lowContrast={true} hideCloseButton={true} />
      </Tile>
    {:else if !$appConfig.loading && !$userSession.loading}
      {#if $userSession.session}
        <RouteHeader />
        <BasicGrid>
          <Route path={routes.organizationCreate}>
            <CreateOrganization />
          </Route>
          <Route path={routes.organizationDetails} let:params>
            {#key params.organizationId}
              <OrganizationDetails organizationId={params.organizationId} />
            {/key}
          </Route>
          <Route path={routes.organizations}>
            <OrganizationList />
          </Route>
          <Route path={routes.groups}>
            <GroupList />
          </Route>
          <Route path={routes.groupCreate}>
            <CreateGroup />
          </Route>
          <Route path={routes.groupDetails} let:params>
            {#key params.groupId}
              <GroupDetails groupId={params.groupId} />
            {/key}
          </Route>
          <Route path={routes.tasks}>
            <TaskModule />
          </Route>
          <Route path={routes.customerBuilds}>
            <CustomerBuilds />
          </Route>
          <Route path={routes.userDetails} let:params>
            {#key params.userId}
              <UserDetails userId={params.userId} />
            {/key}
          </Route>
          <Route path={routes.users}>
            <UserList />
          </Route>
          <Route path={routes.userCreate}>
            <CreateUser />
          </Route>
          <Route path={routes.dataFeeds}>
            <DataFeedModule />
          </Route>
          <Route path={routes.dataFeedDetails} let:params>
            <DataFeedDetails dataFeedId={params.dataFeedId} />
          </Route>
          <Route path={routes.slaTaskDetails} let:params>
            <SlaTaskDetails slaTaskId={params.slaTaskId} />
          </Route>
          <Route path={routes.adminDashboard}>
            <AdminDashboardModule />
          </Route>
          <Route path={routes.objectRestoreCreate}>
            <ObjectRestore />
          </Route>
          <Route path={routes.objectRestoreRequest} let:params>
            <ObjectRestoreApproval jobId={params.jobId} />
          </Route>
          <Route path={routes.licenses}>
            <Licenses />
          </Route>
          <Route path={routes.workbench}>
            <WorkbenchModule />
          </Route>
          <Route path={routes.emails}>
            <Emails />
          </Route>
          <Route path={routes.reports}>
            <ReportsModule />
          </Route>
          <Route path="*">
            <Home />
          </Route>
        </BasicGrid>
      {/if}
    {/if}
  </Layout>
</Router>
